<template>
	<app-section class="menu-section flex flex-col">
		<div class="flex items-center" style="gap: 8px">
			<app-input
				name="search"
				:icon="searching ? 'spin-loading' : 'search'"
				class="flex-1"
				v-model="searchValue"
			/>
			<app-select id="statusSelect" class="w-32" :options="statusOptions" v-model="status" />
			<app-button
				icon="plus"
				class="btn btn-success transparent"
				@click="$emit('createTopping')"
				:disabled="!canEditToppings"
			>
				New Topping
			</app-button>
			<app-button
				icon="plus"
				class="btn btn-success transparent"
				@click="$emit('createToppingItem', selectedTopping)"
				:disabled="!canEditToppings"
			>
				Add Topping Item
			</app-button>
		</div>
		<div class="flex" style="gap: 1px">
			<div class="categories-section">
				<app-draggable
					class="flex flex-col"
					v-model="innerToppings"
					@moved="onToppingMoved"
					handle=".topping-handle"
				>
					<template v-slot="{ item }">
						<div
							:class="[
								'bg-gray-200 border-b-1 border-border-color p-2 mb-px cursor-pointer hover:bg-white flex items-center',
								{
									'bg-gray-300': selectedTopping && selectedTopping.id === item.id,
								},
							]"
							@click="onToppingSelect(item)"
						>
							<app-icon icon="ellipsis-vert" class="cursor-move topping-handle" />
							<span class="ml-2 font-semibold flex-1">{{ item.displayedText }}</span>
							<app-icon icon="pencil" class="cursor-pointer ml-1" @click.stop="editTopping(item)" />
						</div>
					</template>
				</app-draggable>
			</div>
			<div class="flex flex-1 bg-gray-100">
				<div v-if="toppingItems && toppingItems.length" class="flex-1 flex flex-col">
					<div class="flex bg-background px-3 border-b-1 border-border-color mb-px" style="gap: 8px">
						<span
							v-for="column in columns"
							:key="column.key"
							:class="['font-semibold p-2', column.customClass]"
						>
							{{ column.label }}
						</span>
						<span class="w-32 font-semibold p-2">Status</span>
						<span class="w-32 flex-1 font-semibold p-2 text-right">Actions</span>
					</div>
					<app-draggable class="items-section" v-model="toppingItems" @moved="onToppingItemMoved">
						<template v-slot="{ item }">
							<div
								class="flex-1 flex bg-gray-100 border-b-1 border-border-color mb-px hover:bg-white"
								style="gap: 8px"
							>
								<app-icon icon="ellipsis-vert" class="mt-3 -mr-3 pt-px cursor-move" />
								<div v-for="column in columns" :key="column.key" :class="['p-2', column.customClass]">
									{{ item[column.key] }}
								</div>
								<div class="w-32 p-2">
									<app-switch
										v-if="!itemLoaders[item.id]"
										offLabel="Inactive"
										onLabel="Active"
										:model-value="item.status"
										@change="onStatusChange(item, $event)"
									/>
									<app-icon icon="spin-loading" v-else />
								</div>
								<div class="flex flex-1 p-2 items-start justify-end">
									<app-button
										icon="pencil"
										class="btn btn-info transparent small force-small text-13 mr-1"
										:loading="loading"
										@click="updateToppingItem(item)"
									/>
									<!--									<app-button-->
									<!--										icon="trash"-->
									<!--										@click="deleteToppingItemConfirmation(item)"-->
									<!--										class="btn transparent small force-small btn-warning text-13"-->
									<!--									/>-->
								</div>
							</div>
						</template>
					</app-draggable>
				</div>
				<div v-if="!toppingItems.length && !status" class="flex-1 flex items-center justify-center">
					<span>
						No Inactive Items for "
						<b>{{ selectedTopping.menuCategory.categoryName }}</b>
						" category.
					</span>
				</div>
			</div>
		</div>
	</app-section>

	<!--	<app-modal v-if="isDeleteModelVisible" :title="`Delete ${selectedToppingItem.itemName}?`" @close="closeDeleteModal">-->
	<!--		<div class="flex min-w-sm justify-end">-->
	<!--			<app-button-->
	<!--				icon="trash"-->
	<!--				class="flex-1 btn btn-primary mr-1"-->
	<!--				:loading="loadingDelete"-->
	<!--				@click="deleteToppingItem"-->
	<!--			>-->
	<!--				Delete-->
	<!--			</app-button>-->
	<!--			<app-button icon="cancel" class="flex-1 btn btn-primary transparent" @click="closeDeleteModal">-->
	<!--				Cancel-->
	<!--			</app-button>-->
	<!--		</div>-->
	<!--	</app-modal>-->
</template>

<script>
	import AppInput from '@/components/app/AppInput';
	import AppSelect from '@/components/app/AppSelect';
	import AppIcon from '@/components/app/AppIcon';
	import AppSwitch from '@/components/app/AppSwitch';
	import AppDraggable from '@/components/app/AppDraggable';
	import BusinessService from '../../../services/sales/BusinessService';
	import UtilityService from '../../../services/UtilityService';

	const ALL_STATUS = 100;

	export default {
		name: 'MenuTable',
		components: { AppDraggable, AppSwitch, AppIcon, AppSelect, AppInput },
		props: {
			toppings: {
				type: Array,
				required: true,
			},
			loading: {
				type: Boolean,
				default: false,
			},
		},
		data: () => ({
			searching: false,
			loadingDelete: false,
			isDeleteModelVisible: false,
			searchValue: '',
			status: ALL_STATUS,
			selectedTopping: {},
			selectedToppingItem: {},
			itemLoaders: {},
			columns: [
				{ key: 'itemName', label: 'Items', customClass: 'w-72' },
				{ key: 'price', label: 'Price', customClass: 'w-32' },
				{ key: 'oldPrice', label: 'Discounted Price', customClass: 'w-32' },
			],
			statusOptions: [
				{ label: 'All', value: ALL_STATUS },
				{ label: 'Active', value: 1 },
				{ label: 'Inactive', value: 0 },
			],
			items: [],
		}),
		computed: {
			innerToppings: {
				get() {
					if (!this.toppings) {
						return [];
					}
					return this.toppings;
				},
			},
			toppingItems: {
				get() {
					if (this.status === ALL_STATUS) {
						return [...this.items];
					}
					return [...this.items].filter((i) => i.status === this.status);
				},
			},
			canEditToppings() {
				return BusinessService.canEditToppings();
			},
		},
		methods: {
			getToppingItems() {
				this.searching = true;
				BusinessService.getToppingItems(this.businessId, this.selectedTopping.id)
					.then(({ items }) => {
						this.items = items;
					})
					.finally(() => {
						this.searching = false;
					});
			},
			onToppingSelect(topping) {
				this.selectedTopping = topping;
				this.getToppingItems();
			},
			onToppingMoved(event) {
				this.$emit('toppingMoved', event);
			},
			onToppingItemMoved({ oldIndex, newIndex }) {
				this.items = UtilityService.reOrderItems(this.items, oldIndex, newIndex);
				this.$notificationService.info('No API endpoint yet');
			},
			createToppingItem() {
				this.$emit('createToppingItem');
			},
			updateToppingItem(item) {
				this.$emit('updateToppingItem', item);
			},
			// deleteToppingItemConfirmation(item) {
			// 	this.isDeleteModelVisible = true;
			// 	this.selectedToppingItem = item;
			// },
			// deleteToppingItem() {
			// 	this.isDeleteModelVisible = false;
			// 	this.$notificationService.info('No API endpoint yet');
			// },
			// closeDeleteModal() {
			// 	this.isDeleteModelVisible = false;
			// },
			onStatusChange(item) {
				this.itemLoaders[item.id] = true;
				BusinessService.updateToppingItemStatus(
					this.businessId,
					this.selectedTopping.id,
					item.id,
					item.status ? 0 : 1
				)
					.then(() => {
						const i = this.items.find((i) => i.id === item.id);
						i.status = item.status ? 0 : 1;
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.itemLoaders[item.id] = false;
					});
			},
			editTopping(topping) {
				this.$emit('updateTopping', topping);
			},
		},
		mounted() {
			this.selectedTopping = this.toppings[0] ? this.toppings[0] : {};
			this.getToppingItems();
		},
	};
</script>

<style lang="scss" scoped>
	.menu-section {
		height: calc(100vh - 100px);
	}
	.categories-section {
		@apply border-r-1 border-border-color flex flex-col overflow-y-auto;
		max-height: calc(100vh - 190px);
		min-width: 150px;
	}
	.items-section {
		@apply flex flex-col overflow-y-auto;
		max-height: calc(100vh - 250px);
	}
</style>
